import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { companyService } from '../../../services/companyService';

const STEPS = [
  {
    id: 'site',
    label: 'Create your first site',
    description: 'Set up your first construction site to get started.',
    buttonLabel: 'Create Site',
    route: '/site-setup-wizard',
    icon: 'Building2',
  },
  {
    id: 'worker',
    label: 'Invite your team',
    description: 'Add workers and site managers to your company.',
    buttonLabel: 'Invite Team',
    route: '/invite-users',
    icon: 'UserPlus',
  },
  {
    id: 'checklist',
    label: 'Set up a checklist',
    description: 'Create a compliance checklist or daily task list for your site.',
    buttonLabel: 'Create Checklist',
    route: '/checklist-management',
    icon: 'ClipboardList',
  },
  {
    id: 'documents',
    label: 'Upload your documents',
    description: 'Upload H&S documents, certificates and other compliance documents.',
    buttonLabel: 'Upload Documents',
    route: '/document-management',
    icon: 'FileText',
  },
  {
    id: 'dashboard',
    label: 'You\'re all set',
    description: 'Setup complete — explore your admin dashboard.',
    buttonLabel: 'Go to Dashboard',
    route: '/admin-dashboard',
    icon: 'LayoutDashboard',
  },
];

const AdminOnboardingChecklist = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [completion, setCompletion] = useState({
    site: false,
    worker: false,
    checklist: false,
    documents: false,
    dashboard: false,
  });
  const [loading, setLoading] = useState(true);
  const [shouldShow, setShouldShow] = useState(false);

  useEffect(() => {
    if (userProfile?.company_id) {
      fetchCompletionStatus(userProfile?.company_id);
    }
  }, [userProfile?.company_id]);

  const fetchCompletionStatus = async (companyId) => {
    try {
      const [
        companyRes,
        sitesRes,
        workerRes,
        checklistRes,
        docsRes,
      ] = await Promise.all([
        companyService?.getUserCompany(),
        supabase?.from('sites')?.select('id', { count: 'exact', head: true })?.eq('company_id', companyId),
        supabase?.from('user_profiles')?.select('id', { count: 'exact', head: true })?.eq('company_id', companyId)?.in('role', ['worker', 'site_manager']),
        // NSherjFh PR 8: onboarding checklist-count excludes soft-deleted templates.
        supabase?.from('site_checklists')?.select('id', { count: 'exact', head: true })?.eq('company_id', companyId)?.is('deleted_at', null),
        // NSherjFh PR 2: onboarding doc count excludes soft-deleted docs.
        supabase?.from('documents')?.select('id', { count: 'exact', head: true })?.eq('company_id', companyId)?.is('deleted_at', null),
      ]);

      const company = companyRes?.data;
      const setupComplete = company?.setup_complete || false;

      if (setupComplete) {
        setShouldShow(false);
        setLoading(false);
        return;
      }

      const hasSite = (sitesRes?.count || 0) > 0;
      const hasWorker = (workerRes?.count || 0) > 0;
      const hasChecklist = (checklistRes?.count || 0) > 0;
      const hasDocs = (docsRes?.count || 0) > 0;
      // Dashboard step is complete when all previous steps are done
      const hasDashboard = hasSite && hasWorker && hasChecklist && hasDocs;

      const newCompletion = {
        site: hasSite,
        worker: hasWorker,
        checklist: hasChecklist,
        documents: hasDocs,
        dashboard: hasDashboard,
      };

      setCompletion(newCompletion);

      const allDone = Object.values(newCompletion)?.every(Boolean);
      setShouldShow(!allDone);
    } catch (err) {
      console.error('Onboarding checklist fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !shouldShow) return null;

  const completedCount = Object.values(completion)?.filter(Boolean)?.length;
  const totalCount = STEPS?.length;
  const progressPercent = Math.round((completedCount / totalCount) * 100);

  return (
    <div className="bg-card border border-border rounded-xl shadow-elevation-1 p-5 md:p-6 mb-6 md:mb-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Rocket" size={20} className="text-accent" />
          </div>
          <div>
            <h2 className="text-base md:text-lg font-semibold text-foreground font-heading">
              Get started with SiteProof
            </h2>
            <p className="text-sm text-muted-foreground">
              Complete these steps to finish setting up your account.
            </p>
          </div>
        </div>
        <span className="text-sm font-medium text-muted-foreground whitespace-nowrap self-start sm:self-center">
          {completedCount} of {totalCount} complete
        </span>
      </div>
      {/* Progress bar */}
      <div className="w-full bg-muted rounded-full h-2 mb-5">
        <div
          className="bg-accent h-2 rounded-full transition-all duration-500"
          style={{ width: `${progressPercent}%` }}
        />
      </div>
      {/* Steps */}
      <div className="space-y-3">
        {STEPS?.map((step, index) => {
          const done = completion?.[step?.id];
          // Determine if step is accessible (previous step done or first step)
          const prevDone = index === 0 || completion?.[STEPS?.[index - 1]?.id];
          const isActive = !done && prevDone;
          return (
            <div
              key={step?.id}
              className={`flex items-center gap-4 p-3 rounded-lg border transition-colors ${
                done
                  ? 'bg-success/5 border-success/20'
                  : isActive
                  ? 'bg-accent/5 border-accent/20' :'bg-background border-border opacity-60'
              }`}
            >
              {/* Step number / status icon */}
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  done ? 'bg-success/15' : isActive ? 'bg-accent/15' : 'bg-muted'
                }`}
              >
                {done ? (
                  <Icon name="CheckCircle2" size={18} className="text-success" />
                ) : (
                  <span className={`text-xs font-bold ${isActive ? 'text-accent' : 'text-muted-foreground'}`}>
                    {index + 1}
                  </span>
                )}
              </div>
              {/* Label + description */}
              <div className="flex-1 min-w-0">
                <p
                  className={`text-sm font-medium ${
                    done ? 'text-muted-foreground line-through' : 'text-foreground'
                  }`}
                >
                  {step?.label}
                </p>
                {!done && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {step?.description}
                  </p>
                )}
              </div>
              {/* Action button */}
              {isActive && (
                <Button
                  variant={isActive ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => navigate(step?.route)}
                  className="flex-shrink-0 text-xs"
                >
                  {step?.buttonLabel}
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminOnboardingChecklist;
